/**
 * Advisory sub-roles (`users/{email}.advisoryRole` on role 'advisory' users).
 * CommonJS on purpose, like src/constants/pageVisibility.js: both bundles
 * require it.
 *
 * Provisional Adviser (AR) (Sept 2026): has AR Support's access everywhere
 * (Page Visibility, portal view, default filters, reporting group) but can be
 * a client's Assigned Adviser, be booked for annual reviews, approve advice
 * documents and hold an AR number. On the TPA they are listed with support
 * staff, never in the adviser table. Several role switches fall back to adviser
 * behaviour for an unknown value, so access switches must map through
 * supportAccessRole().
 */

const ADVISORY_ROLES = Object.freeze({
  ADVISER: 'adviser',
  UF_SUPPORT: 'ufSupport',
  AR_SUPPORT: 'arSupport',
  AR_ADVISER: 'arAdviser',
  AR_PROVISIONAL_ADVISER: 'arProvisionalAdviser',
  MANAGEMENT: 'management',
  COMPLIANCE: 'compliance',
});

const ADVISORY_ROLE_LABELS = Object.freeze({
  adviser: 'Adviser',
  ufSupport: 'UF Support',
  arSupport: 'AR Support',
  arAdviser: 'AR Adviser',
  arProvisionalAdviser: 'Provisional Adviser (AR)',
  management: 'Management',
  compliance: 'Compliance',
});

/** Roles that can be the Assigned Adviser, be booked for ARs, approve advice documents and hold an AR number. */
const ASSIGNED_ADVISER_ROLES = Object.freeze([
  ADVISORY_ROLES.ADVISER,
  ADVISORY_ROLES.AR_ADVISER,
  ADVISORY_ROLES.AR_PROVISIONAL_ADVISER,
]);

/** Onboarding advisers: who client allocation can give a new client to (owner, Oct 16 2026: not AR advisers). */
const ONBOARDING_ADVISER_ROLES = Object.freeze([ADVISORY_ROLES.ADVISER]);

/** TPA adviser table (the whole advisory team acts on the client's authority). */
const TPA_ADVISER_ROLES = Object.freeze([ADVISORY_ROLES.ADVISER, ADVISORY_ROLES.AR_ADVISER]);

/** TPA support-staff list. */
const TPA_SUPPORT_ROLES = Object.freeze([
  ADVISORY_ROLES.UF_SUPPORT,
  ADVISORY_ROLES.AR_SUPPORT,
  ADVISORY_ROLES.AR_PROVISIONAL_ADVISER,
]);

/** Support role whose access a role borrows. */
const SUPPORT_ACCESS_ROLE = Object.freeze({
  arProvisionalAdviser: ADVISORY_ROLES.AR_SUPPORT,
});

/**
 * The advisoryRole to use for access decisions (pages, portal view, default
 * filters, reporting group): a provisional adviser counts as AR Support.
 * @param {string|null|undefined} advisoryRole
 * @returns {string|null|undefined}
 */
function supportAccessRole(advisoryRole) {
  return SUPPORT_ACCESS_ROLE[advisoryRole] || advisoryRole;
}

function isAssignedAdviserRole(advisoryRole) {
  return ASSIGNED_ADVISER_ROLES.indexOf(advisoryRole) !== -1;
}

/** @returns {string} display label, or '' for an empty / unknown value */
function advisoryRoleLabel(advisoryRole) {
  return ADVISORY_ROLE_LABELS[advisoryRole] || '';
}

module.exports = {
  ADVISORY_ROLES,
  ADVISORY_ROLE_LABELS,
  ASSIGNED_ADVISER_ROLES,
  ONBOARDING_ADVISER_ROLES,
  TPA_ADVISER_ROLES,
  TPA_SUPPORT_ROLES,
  supportAccessRole,
  isAssignedAdviserRole,
  advisoryRoleLabel,
};
